<template>
  <q-dialog
    :value="open"
    position="right"
    transition-show="slide-left"
    transition-hide="slide-right"
    class="absolute"
    @hide="closeDialog"
  >
    <q-card v-if="open" class="quick-quote-container">
      <q-card-section class="text-center">
        <div class="text-h6 text-weight-medium">Quick Calculator</div>
      </q-card-section>
      <q-separator />
      <q-card-section class="row q-col-gutter-sm">
        <div class="col-8 q-gutter-y-sm">
          <div class="row q-gutter-x-sm">
            <div class="col">
              <app-location-filterable-card
                context="quick-quote"
                label="Origin"
                v-model="origin"
                :validation="$v.origin"
              />
            </div>
            <div class="col">
              <app-location-filterable-card
                context="quick-quote"
                label="Destination"
                v-model="destination"
                :validation="$v.destination"
              />
            </div>
          </div>
          <div class="q-py-md row justify-between">
            <q-btn
              outline
              color="orange"
              label="Find Similar Loads on Central Dispatch"
              type="a"
              :href="centralDispatchSearch"
              target="__blank"
              :disabled="!origin.city || !destination.city"
            />
            <div>
              <q-btn
                outline
                color="secondary"
                label="View Origin"
                type="a"
                href="#"
                @click.prevent="handleClickViewLocation(origin.zipcode)"
                :disabled="!origin.zipcode"
              />
              <q-btn
                outline
                color="secondary"
                label="View Destination"
                type="a"
                @click.prevent="handleClickViewLocation(destination.zipcode)"
                :disabled="!destination.zipcode"
              />
              <q-btn
                outline
                color="secondary"
                label="View Route"
                type="a"
                @click.prevent="
                  handleClickViewRoute(origin.zipcode, destination.zipcode)
                "
                :href="`javascript:NewWindow=window.open('http://maps.google.com/maps?q=from+${origin.zipcode}+to+${destination.zipcode}','GoogleImg','width=800,height=600,left=0,top=0, toolbar=No,location=No,scrollbars=Yes,status=No,resizable=Yes,fullscreen=No,directories=No,menubar=No,copyhistory=No'); NewWindow.focus(); void(0);`"
                :disabled="!origin.zipcode || !destination.zipcode"
              />
            </div>
          </div>
          <app-vehicle-details-card
            context="quick-quote"
            v-model="vehicles"
            :validation="$v.vehicles"
          />
        </div>
        <div class="col-4">
          <app-transport-pricing-card
            context="quick-quote"
            v-model="transport"
            :running-taq="runningTAQ"
            :validation="$v.transport"
            @run-taq="handleRunTAQ"
          />
          <app-transport-info-card
            context="quick-quote"
            v-model="transport"
            :validation="$v.transport"
          />
          <div class="text-right q-mt-sm">
            <q-btn
              color="primary"
              label="Create Quote"
              @click="handleCreateQuote"
            />
          </div>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import { formatISO } from 'date-fns';
import { required, minLength, minValue } from 'vuelidate/lib/validators';
import graphql from '@client/graphql';
import { extractKeysIntoObject } from '@client/utils/object-helpers';
import AppLocationFilterableCard from '../cards/location-filterable-card';
import AppVehicleDetailsCard from '../cards/vehicle-details-card';
import AppTransportPricingCard from '../cards/transport-pricing-card';
import AppTransportInfoCard from '../cards/transport-info-card';

const defaultValue = {
  origin: {},
  destination: {},
  vehicles: [
    {
      year: null,
      make: null,
      model: null,
      operable: true
    }
  ],
  transport: {
    carrierType: 'OPEN',
    availableDate: formatISO(new Date(), { representation: 'date' }),
    deposit: 0,
    basePrice: 0,
    sizeFee: 0,
    inopFee: 0,
    enclosedFee: 0
  }
};

export default {
  name: 'AppQuickQuoteDialog',
  components: {
    AppLocationFilterableCard,
    AppVehicleDetailsCard,
    AppTransportPricingCard,
    AppTransportInfoCard
  },
  validations: {
    origin: {
      state: { required },
      city: { required },
      zipcode: { required }
    },
    destination: {
      state: { required },
      city: { required },
      zipcode: { required }
    },
    vehicles: {
      required,
      minLength: minLength(1),
      $each: {
        year: { required },
        make: { required },
        model: { required },
        operable: { required }
      }
    },
    transport: {
      basePrice: { required },
      deposit: { required },
      totalPrice: { minValue: minValue(0), required }
    }
  },
  computed: {
    centralDispatchSearch() {
      const origin = `s~%27Pickup~r~50~c~%27${this.origin.city}~st~%27${this.origin.state}`;
      const destination = `s~%27Dropoff~r~50~c~%27${this.destination.city}~st~%27${this.destination.state}`;
      const baseUrl = 'https://app.centraldispatch.com/search?search=~(vC~(mn~1)~tLPW~2~opt~%27all~ofst~0~lmt~50~sF~(~(n~%27pup~d~%27ASC)~(n~%27dMA~d~%27ASC))~dBS~false~sPSO~false~sTOT~false~rT~%27Open';
      return `${baseUrl}~l~(~(${origin})~(${destination})))`;
    }
  },
  data() {
    return {
      open: false,
      runningTAQ: false,

      ...defaultValue
    };
  },
  methods: {
    openDialog() {
      Object.assign(this, defaultValue);
      this.$v.$reset();
      this.open = true;
    },
    closeDialog() {
      this.open = false;
    },
    handleClickViewLocation(zipcode) {
      if (!zipcode) return;
      const NewWindow = window.open(
        `http://maps.google.com/maps?q=${zipcode}`,
        'GoogleImg',
        'width=800,height=600,left=0,top=0,toolbar=No,location=No,scrollbars=Yes,status=No,resizable=Yes,fullscreen=No,directories=No,menubar=No,copyhistory=No'
      );
      NewWindow.focus();
    },
    handleClickViewRoute(originZipcode, destinationZipcode) {
      if (!originZipcode || !destinationZipcode) return;
      const NewWindow = window.open(
        `http://maps.google.com/maps?q=from+${originZipcode}+to+${destinationZipcode}'`,
        'GoogleImg',
        'width=800,height=600,left=0,top=0,toolbar=No,location=No,scrollbars=Yes,status=No,resizable=Yes,fullscreen=No,directories=No,menubar=No,copyhistory=No'
      );
      NewWindow.focus();
    },
    async handleRunTAQ() {
      this.validate(['origin', 'destination', 'vehicles']);

      this.runningTAQ = true;

      try {
        const response = await this.$apollo.mutate({
          mutation: graphql.mutations.runTransportAutoQuoter,
          variables: {
            origin: this.origin,
            destination: this.destination,
            vehicles: this.vehicles,
            carrierType: this.transport.carrierType
          }
        });
        const { runTransportAutoQuoter } = response.data;
        this.transport = { ...this.transport, ...runTransportAutoQuoter };
      } catch (error) {
        this.notifyNegative(error.message);
      }

      this.runningTAQ = false;
    },
    handleCreateQuote() {
      this.validate(['origin', 'destination', 'vehicles', 'transport']);

      const inputData = extractKeysIntoObject(this, [
        'origin',
        'destination',
        'vehicles',
        'transport'
      ]);

      localStorage.setItem('quick-quote', JSON.stringify(inputData));

      this.closeDialog();
      this.$router.push({
        name: 'create-quote',
        query: { referrer: 'quick-quote' }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.quick-quote-container {
  min-width: calc(100vw - 230px);
}
</style>
